import {
  nextTick,
  onMounted,
  onUnmounted,
  watch,
  type ComponentPublicInstance,
  type Ref,
} from 'vue'

const FALLBACK_HEADER_PX = 56
const OFFSET_VAR = '--galidancia-tabs-header-offset'
const EFFECTIVE_TOP_VAR = '--galidancia-tabs-effective-top'

function resolveElement(target: unknown): HTMLElement | null {
  if (!target) return null
  if (target instanceof HTMLElement) return target
  if (target instanceof Element) return target as HTMLElement
  if (typeof target === 'object' && target !== null && '$el' in target) {
    const el = (target as ComponentPublicInstance).$el
    if (el instanceof HTMLElement) return el
    if (el && (el as Node).nodeType === Node.COMMENT_NODE) {
      return (el as Comment).parentElement
    }
  }
  return null
}

function resolveTagged(target: unknown, tagName: string): HTMLElement | null {
  const el = resolveElement(target)
  if (!el) return null
  if (el.tagName.toLowerCase() === tagName) return el
  const found = el.closest?.(tagName) || el.querySelector?.(tagName)
  return (found as HTMLElement) || el
}

/** Distancia page-top → borde inferior del header (web + Android). */
function measureTabsTop(header: HTMLElement, tabs: HTMLElement): number {
  const page = (tabs.closest('.ion-page') as HTMLElement | null) || null
  const pageTop = page ? page.getBoundingClientRect().top : 0
  const headerBottom = header.getBoundingClientRect().bottom
  return Math.max(FALLBACK_HEADER_PX, Math.ceil(headerBottom - pageTop))
}

/**
 * Coloca ion-tabs bajo el header. Evita bucles de ResizeObserver
 * (cambios de layout diferidos + skip si el offset no cambia).
 */
export function useTabsBelowHeader(
  headerRef: Ref<unknown>,
  tabsRef: Ref<unknown>,
  isActive: () => boolean,
): void {
  let resizeObserver: ResizeObserver | null = null
  let observedNodes: Element[] = []
  let appClassObserver: MutationObserver | null = null
  let rafId = 0
  let lastAppliedTop = -1

  const clearTabsInline = (tabs: HTMLElement | null) => {
    if (!tabs) return
    tabs.style.removeProperty('top')
    tabs.style.removeProperty('height')
    tabs.style.removeProperty('bottom')
  }

  const publish = (px: number) => {
    const value = `${px}px`
    document.documentElement.style.setProperty(OFFSET_VAR, value)
    document.documentElement.style.setProperty(EFFECTIVE_TOP_VAR, value)
  }

  const updateNow = (): void => {
    const header = resolveTagged(headerRef.value, 'ion-header')
    const tabs = resolveTagged(tabsRef.value, 'ion-tabs')
    const active = isActive()

    if (!active) {
      if (lastAppliedTop !== -1) {
        lastAppliedTop = -1
        publish(FALLBACK_HEADER_PX)
        clearTabsInline(tabs)
      }
      return
    }

    const topPx =
      header && tabs ? measureTabsTop(header, tabs) : FALLBACK_HEADER_PX

    if (topPx === lastAppliedTop && tabs?.style.top) {
      return
    }
    lastAppliedTop = topPx
    publish(topPx)

    if (tabs) {
      tabs.style.setProperty('top', `${topPx}px`, 'important')
      tabs.style.setProperty('height', `calc(100% - ${topPx}px)`, 'important')
      tabs.style.setProperty('bottom', '0px', 'important')
    }
  }

  /** Diferir fuera del callback de ResizeObserver evita el error de bucle. */
  const scheduleUpdate = (): void => {
    if (rafId) return
    rafId = window.requestAnimationFrame(() => {
      rafId = 0
      updateNow()
    })
  }

  const unobserveAll = () => {
    if (!resizeObserver) return
    for (const node of observedNodes) {
      resizeObserver.unobserve(node)
    }
    observedNodes = []
  }

  const observeTargets = () => {
    if (!resizeObserver) return
    unobserveAll()
    const header = resolveTagged(headerRef.value, 'ion-header')
    const tabs = resolveTagged(tabsRef.value, 'ion-tabs')
    // Solo observar el header: observar tabs provocaba bucle al cambiar top/height.
    if (header instanceof Element) {
      resizeObserver.observe(header)
      observedNodes.push(header)
    }
    scheduleUpdate()
  }

  onMounted(() => {
    resizeObserver = new ResizeObserver(() => scheduleUpdate())
    void nextTick(() => {
      observeTargets()
      window.setTimeout(() => scheduleUpdate(), 50)
      window.setTimeout(() => scheduleUpdate(), 250)
    })

    window.addEventListener('resize', scheduleUpdate)
    window.addEventListener('orientationchange', scheduleUpdate)

    const app = document.querySelector('ion-app')
    if (app) {
      appClassObserver = new MutationObserver(() => scheduleUpdate())
      appClassObserver.observe(app, { attributes: true, attributeFilter: ['class'] })
    }
  })

  onUnmounted(() => {
    if (rafId) {
      window.cancelAnimationFrame(rafId)
      rafId = 0
    }
    unobserveAll()
    resizeObserver?.disconnect()
    resizeObserver = null
    appClassObserver?.disconnect()
    appClassObserver = null
    window.removeEventListener('resize', scheduleUpdate)
    window.removeEventListener('orientationchange', scheduleUpdate)
    clearTabsInline(resolveTagged(tabsRef.value, 'ion-tabs'))
    lastAppliedTop = -1
    publish(FALLBACK_HEADER_PX)
  })

  watch(
    () => isActive(),
    () => {
      lastAppliedTop = -1
      void nextTick(() => observeTargets())
    },
  )

  watch(headerRef, () => {
    lastAppliedTop = -1
    void nextTick(() => observeTargets())
  })

  watch(tabsRef, () => {
    lastAppliedTop = -1
    void nextTick(() => observeTargets())
  })
}
